// IdentityCard — sidebar card surfacing nationality, passport(s), visa
// status, and languages. Languages render as plain pills (Tier-1 content
// batch dropped the proficiency dimension from player_languages;
// player.nationality stays as a single text field).
//
// Passports: the Tier-1 `passports` display list (player_passports) takes
// precedence when non-empty; otherwise we fall back to the legacy
// eligibility row's passport_country so pre-batch profiles keep their
// Passport line. Never both — one row, one source.
//
// "Visa status" is colored red whenever the freeform text contains a
// negative cue (requires/required/needed/no permit). Otherwise it stays
// neutral. Eligibility rows other than the first are ignored in this card;
// the hero already shows passport as a badge.

interface Eligibility {
  passport_country: string
  visa_status: string | null
  valid_until: string | null
}

export default function IdentityCard({
  nationality,
  eligibility,
  languages,
  passports,
}: {
  nationality: string | null
  eligibility: Eligibility[]
  languages: string[]
  passports: string[]
}) {
  const primary = eligibility[0] ?? null
  const visaStr = primary?.visa_status ?? null
  const visaRestricted =
    visaStr != null &&
    /\b(requires|required|needs|need|no permit|not eligible)\b/i.test(visaStr)
  const passportValue =
    passports.length > 0
      ? passports.join(', ')
      : primary?.passport_country ?? null
  const hasContent =
    !!nationality || !!passportValue || !!visaStr || languages.length > 0

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-4">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-hoop-accent mb-3">
        Identity
      </h2>

      {/* The empty-state message lives OUTSIDE the <dl> — a <dl> may only
          directly contain dt/dd/div, so a bare <p> inside it is an axe
          "definition-list" serious violation (hit on most discovery
          profiles, which carry no identity rows). */}
      {hasContent ? (
        <dl className="space-y-2 text-xs">
          {nationality && <Row label="Nationality" value={nationality} />}
          {passportValue && (
            <Row
              label={passports.length > 1 ? 'Passports' : 'Passport'}
              value={passportValue}
            />
          )}
          {visaStr && (
            <Row
              label="Visa status"
              value={visaStr}
              tone={visaRestricted ? 'red' : 'default'}
            />
          )}
          {languages.length > 0 && (
            <div>
              <dt className="text-hoop-text-3 mb-1.5">
                Languages
              </dt>
              <dd className="flex flex-wrap gap-1.5">
                {languages.map((l) => (
                  <span
                    key={l}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-hoop-bg text-hoop-text-2 border border-hoop-border"
                  >
                    {l}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="text-xs text-hoop-text-3">No identity info yet.</p>
      )}
    </section>
  )
}

function Row({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: string
  tone?: 'default' | 'red'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-hoop-text-3 shrink-0">{label}</dt>
      <dd
        className={
          tone === 'red'
            ? 'text-right font-medium text-hoop-red'
            : 'text-right font-medium text-hoop-text'
        }
      >
        {value}
      </dd>
    </div>
  )
}
